import cn from 'classnames';
import { type MemoExoticComponent, memo, type ReactNode } from 'react';

import type { AvatarShape } from '@didaskill/types/web';

import type { TYPOGRAPHY_VARIANTS } from '../../../config/typography/constants';
import { Typography } from '../../general/typography/Typography';
import { Avatar } from '../avatar/Avatar';
import { AVATAR_SIZES } from '../avatar/constants';
import * as styles from './AvatarInline.styles';

const TYPOGRAPHY_SIZE_MAPPING: Record<AVATAR_SIZES, TYPOGRAPHY_VARIANTS> = {
  [AVATAR_SIZES.SMALL]: Typography.VARIANTS.BODY_MEDIUM_REGULAR,
  [AVATAR_SIZES.MEDIUM]: Typography.VARIANTS.BODY_MEDIUM_BOLD,
  [AVATAR_SIZES.LARGE]: Typography.VARIANTS.BODY_MEDIUM_BOLD,
  [AVATAR_SIZES.OVERSIZED]: Typography.VARIANTS.BODY_MEDIUM_BOLD,
};

export type AvatarInlineProps = {
  /** User to whom the avatar belongs. */
  user: AvatarShape;
  /** Status of the user. */
  status?: ReactNode;
  /** Avatar size. */
  size?: AVATAR_SIZES;
  /** Display light text. */
  light?: boolean;
  /** Use default didaskill picture */
  useDefaultAvatar?: boolean;
  /** Root classname. */
  className?: string;
};

export const AvatarInlineBase = ({
  user,
  status = null,
  size = Avatar.SIZES.MEDIUM,
  light = false,
  useDefaultAvatar = false,
  className = null,
}: AvatarInlineProps) => (
  <div css={styles.container({ hasStatus: !!status })} className={className}>
    <Avatar user={user} size={size} useDefaultAvatar={useDefaultAvatar} />
    <div css={styles.textContainer}>
      <Typography
        variant={TYPOGRAPHY_SIZE_MAPPING[size]}
        css={styles.fullNameText}
        className={cn(size, { isLight: light })}
      >
        {user.fullName}
      </Typography>

      {size === Avatar.SIZES.MEDIUM && status && (
        <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR} css={styles.statusText}>
          {status}
        </Typography>
      )}
    </div>
  </div>
);

AvatarInlineBase.displayName = 'AvatarInline';

export const AvatarInline: MemoExoticComponent<typeof AvatarInlineBase> & {
  SIZES?: typeof AVATAR_SIZES;
} = memo(AvatarInlineBase);

AvatarInline.SIZES = AVATAR_SIZES;
